/*
 * Copyright (c) 2024, King Living
 * Map detected ISO country (e.g. from /server/geolocate) to storefront site + default locale.
 */

import {SITE_CONFIG} from '@amblique/king-living/app/constants/site-config';
import {HOME_HREF} from '@salesforce/retail-react-app/app/constants';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import {getSites} from '@salesforce/retail-react-app/app/utils/site-utils';
import {createUrlTemplate} from '@salesforce/retail-react-app/app/utils/url';

/**
 * @param {string|null|undefined} detectedCountryCode - ISO 3166-1 alpha-2 from geolocation
 * @returns {Object|null} Entry from SITE_CONFIG or null
 */
export function getSiteConfigForDetectedCountry(detectedCountryCode) {
  const code = detectedCountryCode?.toUpperCase();

  if (!code) return null;

  for (const config of Object.values(SITE_CONFIG)) {
    if (config.countryCode === code) {
      return config;
    }
  }

  if (code === 'UK' || code === 'GB') {
    return SITE_CONFIG['kl-uk'] || null;
  }

  return null;
}

/**
 * Build a path on the target regional site (same createUrlTemplate rules as home: default locale omitted when showDefaults is false).
 *
 * @param {{ siteId: string }} siteConfigEntry - SITE_CONFIG entry or picker row from getRegionPickerSites
 * @param {string} pathAndQuery - App path + optional query, e.g. `/product/SKU` or `/search?q=a` (no site/locale prefix)
 * @returns {string|null}
 */
export function buildRegionSiteUrl(siteConfigEntry, pathAndQuery) {
  if (!siteConfigEntry?.siteId) return null;

  let path = pathAndQuery == null ? HOME_HREF : String(pathAndQuery);

  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  let sites;

  try {
    sites = getSites();
  } catch {
    return null;
  }

  const targetSite = sites.find((s) => s.id === siteConfigEntry.siteId);

  if (!targetSite?.l10n?.defaultLocale) return null;

  const siteRef = targetSite.alias || targetSite.id;
  const localeRef = targetSite.l10n.defaultLocale;

  try {
    const {app: appConfig} = getConfig();

    if (!appConfig?.url) return null;

    const buildForTarget = createUrlTemplate(appConfig, siteRef, localeRef);

    return buildForTarget(path, siteRef, localeRef);
  } catch {
    return null;
  }
}

/**
 * Home URL for the target regional site. Uses createUrlTemplate for that site + its default locale so
 * showDefaults:false omits the default locale from the path (useMultiSite().buildUrl is tied to the
 * current site and wrongly keeps /en-CA when switching from another site).
 *
 * @param {{ siteId: string, defaultLocale?: string }} siteConfigEntry - SITE_CONFIG entry or picker row from getRegionPickerSites
 * @returns {string|null}
 */
export function buildRegionHomeUrl(siteConfigEntry) {
  return buildRegionSiteUrl(siteConfigEntry, HOME_HREF);
}

/** Map app / SFCC config codes to ISO 3166-1 alpha-2 for Intl.DisplayNames. */
function normalizeSfccCountryCodeToIso3166(code) {
  const u = String(code).toUpperCase();

  if (u === 'UK') return 'GB';

  return u;
}

/**
 * @param {object} site - Entry from getSites()
 * @param {Intl.DisplayNames} displayNames
 * @returns {{ siteId: string, defaultLocale: string, country: string }|null}
 */
function siteToPickerRow(site, displayNames) {
  if (!site?.countryCode || !site?.l10n?.defaultLocale) return null;

  const iso = normalizeSfccCountryCodeToIso3166(site.countryCode);
  let country;

  try {
    country = displayNames.of(iso);
  } catch {
    country = null;
  }

  if (!country) {
    country = SITE_CONFIG[site.id]?.country || site.id;
  }

  return {
    siteId: site.id,
    defaultLocale: site.l10n.defaultLocale,
    country,
  };
}

/**
 * Row-major order for the two-column region picker (Figma King Web Replatforming, menu list 13157:43904).
 * Sites returned by getSites() but not listed here are appended after, sorted by site id.
 */
const REGION_PICKER_SITE_ORDER = [
  'kl-au',
  'kl-nz',
  'kl-uk',
  'kl-ca',
  'kl-us',
  'kl-sg',
  'kl-my',
];

function sortPickerRowsByDesignOrder(rows) {
  const orderIndex = new Map(REGION_PICKER_SITE_ORDER.map((siteId, i) => [siteId, i]));
  const fallbackStart = REGION_PICKER_SITE_ORDER.length;

  return [...rows].sort((a, b) => {
    const ai = orderIndex.has(a.siteId) ? orderIndex.get(a.siteId) : fallbackStart;
    const bi = orderIndex.has(b.siteId) ? orderIndex.get(b.siteId) : fallbackStart;

    if (ai !== bi) return ai - bi;

    return String(a.siteId).localeCompare(String(b.siteId));
  });
}

/**
 * Region picker rows: all storefront sites, display names localized, order aligned to design grid.
 *
 * @param {string} [locale] - BCP 47 locale for display names (e.g. react-intl locale)
 * @returns {Array<{ siteId: string, defaultLocale: string, country: string }>}
 */
export function getRegionPickerSites(locale = 'en') {
  let sites;

  try {
    sites = getSites();
  } catch {
    return [];
  }

  const displayNames = new Intl.DisplayNames([locale], {type: 'region'});

  const rows = sites.map((s) => siteToPickerRow(s, displayNames)).filter(Boolean);

  return sortPickerRowsByDesignOrder(rows);
}
